/**
 * Pure helpers behind the Faro facade (src/observability/faro.ts). Kept free of
 * any Faro, Vue or store import so they can be unit tested on their own.
 */
export type FaroEnv = Partial<
  Record<
    | 'VITE_FARO_ENABLED'
    | 'VITE_FARO_COLLECTOR_URL'
    | 'VITE_FARO_ENVIRONMENT'
    | 'VITE_FARO_COUNTRY'
    | 'VITE_APP_VERSION'
    | 'MODE',
    string
  >
>

export type FaroConfig = {
  enabled: boolean
  url: string | null
  version: string
  environment: string
  country: string | null
}

/** Tenant domains end with the country the server belongs to; anything else is unknown. */
const COUNTRY_BY_TLD: Record<string, string> = { be: 'BE', fr: 'FR', ch: 'CH' }

export type FaroUser = {
  id: string
  attributes: { tenant: string; role: string; roles: string }
}

export type MenuEntry = { name: string; key: string; path: string | null }

export type PageContext = {
  route: string
  page_key: string | null
  section_key: string | null
}

/** Structural slice of a vue-router route (a RouteLocationNormalized fits). */
type RouteLike = {
  name?: unknown
  path?: string
  meta?: Record<string | number | symbol, unknown>
  matched?: { name?: unknown }[]
}

type ComponentOptionsLike = { name?: string; __name?: string } | undefined

/**
 * The environment comes from the build (VITE_FARO_ENVIRONMENT, set by the
 * build:prod / build:staging scripts), never from the Vite mode: production is
 * built with `--mode staging`, which used to tag every production trace as
 * staging. The country is passed by the build when known (one server per
 * country) and otherwise read from the tenant domain.
 */
export function readFaroConfig(env: FaroEnv, hostname = ''): FaroConfig {
  const url = env.VITE_FARO_COLLECTOR_URL || null
  return {
    enabled: env.VITE_FARO_ENABLED === 'true' && Boolean(url),
    url,
    version: env.VITE_APP_VERSION || 'unknown',
    environment: env.VITE_FARO_ENVIRONMENT || env.MODE || 'unknown',
    country: env.VITE_FARO_COUNTRY || countryFromHostname(hostname)
  }
}

export function countryFromHostname(hostname: string): string | null {
  const topLevelDomain = hostname.toLowerCase().split('.').pop() ?? ''
  return COUNTRY_BY_TLD[topLevelDomain] ?? null
}

/**
 * Faro user payload: the id only, never the email or the name (they would sit
 * in Loki for 13 months), correlated with the user through the database.
 * Faro attributes must be strings, hence roles joined.
 */
export function buildFaroUser(
  user: { id?: string | number; roles?: unknown } | null | undefined,
  tenant: string | null | undefined
): FaroUser | null {
  if (!user?.id) {
    return null
  }
  const roles = Array.isArray(user.roles) ? user.roles : []
  return {
    id: String(user.id),
    attributes: {
      tenant: tenant || 'unknown',
      role: roles[0] || 'none',
      roles: roles.join(',')
    }
  }
}

/**
 * Side menu entries with their resolved path, used to attach a menu section to
 * any route (including sub-pages that are not menu entries themselves).
 */
export function buildMenuIndex(
  menu: { pageName: string; title: string }[],
  resolvePath: (pageName: string) => string | null
): MenuEntry[] {
  return menu.map((entry) => ({
    name: entry.pageName,
    key: entry.title,
    path: resolvePath(entry.pageName)
  }))
}

export function resolvePageContext(
  route: RouteLike | null | undefined,
  menuIndex: MenuEntry[]
): PageContext {
  const routeName = typeof route?.name === 'string' ? route.name : 'unknown'
  const matchedNames = (route?.matched || []).map((record) => record.name)
  const byName = menuIndex.find((entry) => matchedNames.includes(entry.name))
  const byPath = byName
    ? null
    : menuIndex
        .filter(
          (entry) =>
            entry.path &&
            entry.path !== '/' &&
            (route?.path === entry.path ||
              route?.path?.startsWith(`${entry.path}/`))
        )
        .sort((a, b) => b.path.length - a.path.length)[0]
  // Last resort: same first path segment (e.g. /settings/accounting-code/new
  // belongs to the section whose menu entry is /settings/user).
  const firstSegment = (path: string | null | undefined) =>
    (path || '').split('/')[1] || null
  const bySegment =
    byName || byPath
      ? null
      : menuIndex.find(
          (entry) =>
            entry.path &&
            firstSegment(entry.path) &&
            firstSegment(entry.path) === firstSegment(route?.path)
        )
  const section = byName || byPath || bySegment || null

  return {
    route: routeName,
    page_key:
      (route?.meta?.analyticsKey as string | undefined) ||
      (byName ? byName.key : null),
    section_key: section ? section.key : null
  }
}

/**
 * Faro only reads data-faro-user-action-name on the exact click target, but
 * clicks usually land on a child of the tagged button (label span, icon).
 * Returns the action name of the tagged ancestor for such nested targets, and
 * null for direct hits (handled natively by Faro) or untagged areas.
 */
export function resolveUserActionName(target: unknown): string | null {
  // Duck-typed on purpose: icons are SVG elements, not HTMLElement.
  const element = target as (Element & HTMLOrSVGElement) | null
  if (!element?.dataset || typeof element.closest !== 'function') {
    return null
  }
  if (element.dataset.faroUserActionName) {
    return null
  }
  const tagged = element.closest<HTMLElement | SVGElement>(
    '[data-faro-user-action-name]'
  )
  return tagged?.dataset?.faroUserActionName || null
}

/**
 * requestAnimationFrame is suspended while a tab is hidden (ctrl+click, tab
 * switch): such navigations would report durations of minutes and pollute the
 * lazy-loading before/after percentiles.
 */
export function isMeasurableNavigation(
  visibilityAtStart: DocumentVisibilityState,
  visibilityAtEnd: DocumentVisibilityState
): boolean {
  return visibilityAtStart === 'visible' && visibilityAtEnd === 'visible'
}

export function resolveModalName(
  trackingName: string | undefined,
  parentOptions: ComponentOptionsLike
): string {
  return (
    trackingName || parentOptions?.name || parentOptions?.__name || 'unknown'
  )
}

/**
 * Reverse index of the loaded translations: displayed text -> i18n key, so a
 * button label becomes a stable, locale independent key (button.save rather
 * than "Enregistrer" / "Opslaan"). Keys under button.* win over any other
 * namespace holding the same text.
 */
export function buildTranslationIndex(messages: object): Map<string, string> {
  const index = new Map<string, string>()
  const visit = (node: object | undefined, prefix: string) => {
    Object.entries(node || {}).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key
      if (value && typeof value === 'object') {
        visit(value, path)
        return
      }
      if (typeof value !== 'string' || value.trim() === '') {
        return
      }
      const text = value.trim().toLowerCase()
      const current = index.get(text)
      if (
        !current ||
        (!current.startsWith('button.') && path.startsWith('button.'))
      ) {
        index.set(text, path)
      }
    })
  }
  visit(messages, '')
  return index
}

/**
 * Tracking name of a button: "<owner component>#<label key>". Untranslated
 * titles (dynamic text, possibly personal data) are never sent: the icon or
 * "unlabelled" is used instead.
 */
export function resolveButtonName(
  {
    owner,
    title,
    icon
  }: { owner: string | null; title?: unknown; icon?: string | null },
  translationIndex: Map<string, string>
): string {
  const key =
    typeof title === 'string'
      ? translationIndex.get(title.trim().toLowerCase())
      : undefined
  const label = key || (icon ? `icon:${icon}` : 'unlabelled')
  return `${owner || 'unknown'}#${label}`
}

/**
 * Component whose template renders the button. A button passed through a
 * slot is mounted under the slot host (BaseModal...), but its vnode keeps the
 * rendering instance in ctx, which is the component a developer looks for.
 */
export function resolveOwnerName(
  instance:
    | {
        vnode?: { ctx?: { type?: ComponentOptionsLike } | null }
        parent?: { type?: ComponentOptionsLike } | null
      }
    | null
    | undefined
): string | null {
  const options = instance?.vnode?.ctx?.type || instance?.parent?.type
  return options?.name || options?.__name || null
}
